import CheckIcon from "@/assets/CheckIcon";
import CrownIcon from "@/assets/CrownIcon";
import StopIcon from "@/assets/StopIcon";
import TeamPositionIcon from "@/assets/TeamPositionIcon";
import TraceBackIcon from "@/assets/TraceBackIcon";
import TraceFollowIcon from "@/assets/TraceFollowIcon";
import TraceIcon from "@/assets/TraceIcon";
import TocIcon from "@mui/icons-material/Toc";
import useMissions from "@/hooks/useMissions";
import useUsers from "@/hooks/useUsers";
import useGlobalStore from "@/services/store/useGlobalStore";
import { logger } from "@/utils/functions/logger";
import CommandButton from "@components/CommandButton";
import NotesIcon from "@mui/icons-material/Notes";
import { Box, Stack } from "@mui/material";
import { ChangeEvent, RefObject, useState } from "react";
import MessageIcon from "@mui/icons-material/Message";
import MessageSelectorPopup from "./components/MessageSelectorPopup";
import StatusPopup from "@components/StatusPopup/StatusPopup";
import { Permission } from "@/utils/constants/permission";
import { hasPermission } from "@/utils/functions/permission";
import React from "react";

interface ICommand {
  commandRef: RefObject<HTMLDivElement>;
}

const Command: React.FC<ICommand> = ({ commandRef }) => {
  const { startMission, startFollowLeaderMission, stopMission, sendMaskConfig } = useMissions();
  const { usersById, supervisor } = useUsers();
  // const { groupsById } = useGroups();

  const selectedUsers = useGlobalStore((state) => state.selectedUsers);
  const selectedTraces = useGlobalStore((state) => state.selectedTraceIds);

  const [statusPopupOpened, setStatusPopupOpened] = useState(false);

  const [messageSelectorPopupOpened, setMessageSelectorPopupOpened] = useState(false);
  const handleMessageSelectorPopupClose = (mission: MissionType | null) => {
    if (mission) {
      startMission(mission);
    }
    setMessageSelectorPopupOpened(false);
  };

  // take the first
  const [selectedTrace] = selectedTraces;

  const allSelectedUsersNoOnGoingMission = selectedUsers.every(
    (userId: number) => usersById[userId]?.mission?.status !== "ONGOING"
  );
  const allSelectedUsersAreInTrace = selectedUsers.every((userId) => {
    const user = usersById[userId];

    return user?.mission?.type === "TRACE" && user?.mission?.status === "ONGOING";
  });

  const numberOfSelectedUserWithoutLeadership = selectedUsers.filter(
    (userId: number) => !usersById[userId]?.leader
  )?.length;

  const allSelectedUsersBelongToAGroup = selectedUsers.every((userId: number) => usersById[userId]?.group?.id);

  const handleFileChange = (
    event: ChangeEvent<HTMLInputElement>,
    configType: ConfigType,
    // eslint-disable-next-line no-unused-vars
    onContentLoaded: (content: string) => string
  ) => {
    if (event.target.files) {
      const [file] = event.target.files;
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target && e.target.result) {
          try {
            const content: string = e.target.result as string;
            sendMaskConfig(onContentLoaded(content), configType, file.name);
          } catch (e) {
            logger.error(e);
          }
        }
      };
      reader.readAsText(file);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const searchFileInput = (configType: ConfigType, onContentLoaded: (c: string) => string) => {
    const inputElement = document.createElement("input");
    inputElement.hidden;
    inputElement.type = "file";
    inputElement.accept = "*.json";
    inputElement.addEventListener(
      "change",
      (e: Event) => handleFileChange(e as unknown as ChangeEvent<HTMLInputElement>, configType, onContentLoaded) // forced cast
    );
    inputElement.click();
  };

  const showStatusPopup = () => {
    setStatusPopupOpened(true);
  };

  const commands = [
    {
      label: "Stop",
      icon: <StopIcon color="white" />,
      onClick: stopMission,
      cannotClick: !selectedUsers.length,
      tooltip: "Veuillez sélectionner au moins un utilisateur pour démarrer cette mission.",
      permission: Permission.STOP,
    },
    {
      label: "Message",
      icon: <MessageIcon sx={{ fill: "white" }} />,
      onClick: () => setMessageSelectorPopupOpened(true),
      cannotClick: !selectedUsers.length,
      tooltip: "Veuillez sélectionner au moins un utilisateur sans mission en cours pour démarrer cette mission.",
      permission: Permission.MESSAGE,
    },
    {
      label: "Statut",
      icon: <CheckIcon color="white" />,
      onClick: () => showStatusPopup(),
      cannotClick: !selectedUsers.length,
      tooltip: "Veuillez sélectionner au moins un utilisateur sans mission en cours pour démarrer cette mission.",
      permission: Permission.STATUS,
    },
    {
      label: "Trace",
      icon: <TraceIcon color="white" />,
      onClick: () => startMission("TRACE"),
      cannotClick: !selectedUsers.length || allSelectedUsersAreInTrace,
      tooltip: "Veuillez sélectionner au moins un utilisateur sans mission en cours pour démarrer cette mission.",
      permission: Permission.TRACE,
    },
    {
      label: "Trace back",
      icon: <TraceBackIcon color="white" />,
      onClick: () => startMission("TRACE_BACK"),
      cannotClick: !selectedUsers.length || !allSelectedUsersAreInTrace,
      tooltip: "Veuillez sélectionner des utilisateurs ayant une trace pour démarrer cette mission.",
      permission: Permission.TRACE_BACK,
    },
    {
      label: "Trace follow",
      icon: <TraceFollowIcon color="white" />,
      onClick: () => startMission("TRACE_FOLLOW", { traceId: selectedTrace }),
      cannotClick: !selectedUsers.length || !selectedTrace || !allSelectedUsersNoOnGoingMission,
      tooltip: "Veuillez sélectionner un (des) utilisateur(s) et une trace pour démarrer cette mission.",
      permission: Permission.TRACE_FOLLOW,
    },
    {
      label: "Follow leader",
      icon: <CrownIcon color="white" />,
      onClick: () => startFollowLeaderMission(),
      cannotClick: !allSelectedUsersNoOnGoingMission || !numberOfSelectedUserWithoutLeadership,
      tooltip:
        "Veuillez sélectionner des utilisateurs appartenant à un groupe ayant un leader pour démarrer cette mission.",
      permission: Permission.FOLLOW_LEADER,
    },
    {
      label: "Team position",
      icon: <TeamPositionIcon color="white" />,
      onClick: () => startMission("TEAM_POSITION"),
      cannotClick: !allSelectedUsersBelongToAGroup || !allSelectedUsersNoOnGoingMission || !selectedUsers.length,
      tooltip: "Veuillez sélectionner des utilisateurs appartenant à un groupe pour démarrer cette mission.",
      permission: Permission.TEAM_POSITION,
    },
    {
      label: "Grammaire",
      icon: <NotesIcon sx={{ color: (theme: ThemeType) => theme.palette.primary.contrastText }} />,
      onClick: () =>
        searchFileInput("CONFIG_GRAMMAR", (content) => {
          return JSON.stringify(JSON.parse(content));
        }),
      cannotClick: !selectedUsers.length,
      tooltip: "Veuillez sélectionner des utilisateurs pour démarrer cette mission.",
      permission: Permission.SWITCH_GRAMMAR,
    },
    {
      label: "Matrice de LED",
      icon: <TocIcon sx={{ color: (theme: ThemeType) => theme.palette.primary.contrastText }} />,
      onClick: () => searchFileInput("CONFIG_LED_MATRIX", (content) => content),
      cannotClick: !selectedUsers.length,
      tooltip: "Veuillez sélectionner des utilisateurs pour démarrer cette mission.",
      permission: Permission.LED_MATRIX,
    },
  ];

  return (
    <Box height="100%">
      <Stack
        ref={commandRef}
        sx={{
          background: (theme: ThemeType) => theme.palette.secondary.main,
          borderRadius: 2,
          height: "100%",
          padding: "12px",
          boxSizing: "border-box",
          maxHeight: "100%",
          overflowY: "auto",
          overflowX: "hidden",
        }}
        spacing={1}>
        {commands.map((command) => (
          <React.Fragment key={command.label}>
            {(command.permission == null || (supervisor != null && hasPermission(command.permission, supervisor))) && (
              <CommandButton
                isDisabled={command.cannotClick}
                label={command.label}
                icon={command.icon}
                onClick={command.onClick}
                tooltip={command.tooltip}
              />
            )}
          </React.Fragment>
        ))}
      </Stack>

      <MessageSelectorPopup open={messageSelectorPopupOpened} handleClose={handleMessageSelectorPopupClose} />
      <StatusPopup
        open={statusPopupOpened}
        users={selectedUsers.map((userId) => usersById[userId])}
        onClose={() => setStatusPopupOpened(false)}
      />
    </Box>
  );
};

export default Command;
